import { useEffect, useState } from 'react';
import {
  orcamentosApi,
  type OrcamentoDetalhe,
  type StatusOrcamento,
} from '@/features/orcamentos/api/orcamentosApi';
import { ehTerminal } from '@/features/orcamentos/api/etapasKanban';
import { useEtapasKanban } from '@/shared/state/EtapasKanbanContext';
import { Button } from '@/shared/ui/Button';
import { PageHeader } from '@/shared/ui/PageHeader';
import { CriarContaReceberDoOrcamentoModal } from '@/features/financeiro/pages/CriarContaReceberDoOrcamentoModal';
import { EnviarOrcamentoWhatsappModal } from '@/features/pos-venda/components/EnviarOrcamentoWhatsappModal';
import { CardAvaliacaoOS } from '@/features/pos-venda/components/CardAvaliacaoOS';
import { CupomTermico58mm } from '@/features/orcamentos/components/CupomTermico58mm';
import { OrcamentoImpressaoA4 } from '@/features/orcamentos/components/OrcamentoImpressaoA4';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useConfiguracao } from '@/features/configuracoes/hooks/useConfiguracao';
import {
  configuracoesApi,
  diasValidadeLink,
  rotuloDias,
  type DadosEmpresa,
} from '@/features/configuracoes/api/configuracoesApi';
import {
  imprimirComClasse,
  medirElementoComoPageSize,
  salvarComoPdf,
} from '@/shared/print/imprimir';
import { PrintPortal } from '@/shared/print/PrintPortal';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { Modal } from '@/shared/ui/Modal';
import { BotaoOcultarValores } from '@/shared/ui/BotaoOcultarValores';
import { useOcultarValores } from '@/shared/state/OcultarValoresContext';
import { GaleriaFotosOS } from '@/features/captura/components/GaleriaFotosOS';
import { aguardarImagensCarregadas } from '@/features/captura/aguardarImagens';
import type { FotoCapturaDTO } from '@/features/captura/tipos';

// Preferência deste aparelho: sair com as fotos do veículo no A4.
const CHAVE_INCLUIR_FOTOS = 'orcamento:a4:incluir-fotos';

function lerIncluirFotos(): boolean {
  try {
    return localStorage.getItem(CHAVE_INCLUIR_FOTOS) === '1';
  } catch {
    return false;
  }
}

function gravarIncluirFotos(valor: boolean): void {
  try {
    localStorage.setItem(CHAVE_INCLUIR_FOTOS, valor ? '1' : '0');
  } catch {
    // armazenamento bloqueado: vale só nesta abertura
  }
}

/** Fotos no papel: espera carregar antes de abrir o diálogo (teto de 5s). */
function aguardarFotosDaImpressao(): Promise<void> {
  return aguardarImagensCarregadas(document.querySelector('.print-portal .impressao-a4'), 5000);
}

type Props = {
  id: string;
  aoVoltar: () => void;
  aoEditar: (id: string) => void;
};

const formatadorMoedaReal = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

// Bandeira module-level lida pelos helpers de format desta tela —
// `OrcamentoVisualizacaoPage` sincroniza no início do render com o
// contexto `useOcultarValores`. Igual ao padrão de FinanceiroPage e
// RelatoriosPage; evita threadar `ocultar` por cada subcomponente.
let __orcamentoVisualizacaoOcultar = false;
const formatadorMoeda = {
  format(v: number): string {
    return __orcamentoVisualizacaoOcultar
      ? 'R$ ●●●●'
      : formatadorMoedaReal.format(v || 0);
  },
};

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatarData(iso: string) {
  return formatadorData.format(new Date(iso));
}

function formatarDesconto(tipo: string | null, valor: number | null): string {
  if (!tipo || valor == null || valor === 0) return '—';
  return tipo === 'Percentual' ? `${valor}%` : formatadorMoeda.format(valor);
}

export function OrcamentoVisualizacaoPage({ id, aoVoltar, aoEditar }: Props) {
  const { ocultar } = useOcultarValores();
  __orcamentoVisualizacaoOcultar = ocultar;
  const { etapas, rotuloDe } = useEtapasKanban();
  const [detalhe, setDetalhe] = useState<OrcamentoDetalhe | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [criandoContaReceber, setCriandoContaReceber] = useState(false);
  const [preVisualizandoCupom, setPreVisualizandoCupom] = useState(false);
  const [preVisualizandoA4, setPreVisualizandoA4] = useState(false);
  const [enviandoWhatsapp, setEnviandoWhatsapp] = useState(false);
  const [gerandoLink, setGerandoLink] = useState(false);
  const [linkMsg, setLinkMsg] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);
  // Link público usado no QR code do cupom 58mm. Guardado aqui para não
  // pedir um token novo a cada abertura da pré-visualização.
  const [linkAcompanhamento, setLinkAcompanhamento] = useState<string | null>(null);
  const [linkCupomFalhou, setLinkCupomFalhou] = useState(false);
  const { estado } = useAuth();
  const nomeEmpresa = estado?.conta?.nomeEmpresa ?? 'Oficina Ágil';
  const { valor: termoA4 } = useConfiguracao('termo_impressao_a4');
  const { valor: termo58mm } = useConfiguracao('termo_impressao_58mm');
  const { valor: validadeLink } = useConfiguracao('validade_link_acompanhamento_dias');
  const [empresa, setEmpresa] = useState<DadosEmpresa | null>(null);
  useEffect(() => {
    let cancelado = false;
    void configuracoesApi
      .obterEmpresa()
      .then((d) => {
        if (!cancelado) setEmpresa(d);
      })
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
  }, []);
  const dialogos = useDialogos();
  const [salvandoStatus, setSalvandoStatus] = useState(false);
  const [registrandoKm, setRegistrandoKm] = useState(false);
  const [kmDigitado, setKmDigitado] = useState('');
  const [salvandoKm, setSalvandoKm] = useState(false);
  const [erroKm, setErroKm] = useState<string | null>(null);
  // Fotos do veículo (captura pelo celular), para a impressão A4.
  const [fotosOS, setFotosOS] = useState<FotoCapturaDTO[]>([]);
  const [incluirFotos, setIncluirFotos] = useState(lerIncluirFotos);
  const fotosImpressao = incluirFotos && fotosOS.length > 0 ? fotosOS : undefined;

  function alterarIncluirFotos(valor: boolean) {
    setIncluirFotos(valor);
    gravarIncluirFotos(valor);
  }

  function abrirRegistroKm() {
    if (!detalhe) return;
    setKmDigitado(detalhe.kmRegistrado != null ? String(detalhe.kmRegistrado) : '');
    setErroKm(null);
    setRegistrandoKm(true);
  }

  async function salvarKm() {
    if (!detalhe) return;
    const km = Number(kmDigitado);
    if (kmDigitado.trim() === '' || !Number.isInteger(km) || km < 0 || km > 9_999_999) {
      setErroKm('Informe a quilometragem em números inteiros.');
      return;
    }
    setSalvandoKm(true);
    setErroKm(null);
    try {
      await orcamentosApi.registrarKm(detalhe.id, km);
      // Recarrega: o backend decide se o KM atual do veículo sobe junto.
      setDetalhe(await orcamentosApi.obter(detalhe.id));
      setRegistrandoKm(false);
    } catch (e) {
      setErroKm(e instanceof Error ? e.message : 'Falha ao registrar a quilometragem.');
    } finally {
      setSalvandoKm(false);
    }
  }

  async function copiarLinkAcompanhamento() {
    if (!detalhe) return;
    setGerandoLink(true);
    setLinkMsg(null);
    try {
      const { url } = await orcamentosApi.gerarLinkAcompanhamento(detalhe.id);
      setLinkAcompanhamento(url);
      setLinkCupomFalhou(false);
      try {
        await navigator.clipboard.writeText(url);
        setLinkMsg({ tipo: 'ok', texto: `Link copiado (vale ${rotuloDias(diasValidadeLink(validadeLink))}): ${url}` });
      } catch {
        // Sem permissão de área de transferência: mostra o link para copiar à mão.
        setLinkMsg({ tipo: 'ok', texto: url });
      }
    } catch (e) {
      setLinkMsg({
        tipo: 'erro',
        texto:
          e instanceof Error
            ? e.message
            : 'Falha ao gerar o link. Conecte à internet e sincronize a OS.',
      });
    } finally {
      setGerandoLink(false);
    }
  }

  /**
   * Abre a pré-visualização do cupom e, em paralelo, busca o link público que
   * vira QR code no rodapé. A busca não bloqueia a tela: sem internet (ou com
   * a OS ainda não sincronizada) o cupom sai sem o QR e o modal avisa.
   */
  function abrirPreviaCupom() {
    setPreVisualizandoCupom(true);
    if (!detalhe || linkAcompanhamento) return;
    setLinkCupomFalhou(false);
    orcamentosApi
      .gerarLinkAcompanhamento(detalhe.id)
      .then(({ url }) => setLinkAcompanhamento(url))
      .catch(() => setLinkCupomFalhou(true));
  }

  // Etapa final (fechado pago / cancelado): o select trava, como no kanban.
  const etapaFinal = detalhe ? ehTerminal(detalhe.status) : false;

  async function aoMudarStatus(novo: StatusOrcamento) {
    if (!detalhe || novo === detalhe.status) return;
    setSalvandoStatus(true);
    setErro(null);
    try {
      await orcamentosApi.alterarStatus(detalhe.id, novo);
      // Recarrega: a mudança de status pode dar baixa/estorno de estoque no
      // backend, então buscamos o estado consolidado em vez de só trocar local.
      const atualizado = await orcamentosApi.obter(detalhe.id);
      setDetalhe(atualizado);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao alterar o status.');
    } finally {
      setSalvandoStatus(false);
    }
  }

  function medirCupomPreview() {
    return medirElementoComoPageSize(
      '.preview-impressao--58mm .cupom-58',
      58,
      5,
    );
  }

  async function imprimirCupom() {
    const pageSize = medirCupomPreview();
    const r = await imprimirComClasse('imprimindo-cupom', { pageSize });
    if (!r.ok && !r.cancelado) {
      await dialogos.alertar(r.erro ?? 'Falha ao imprimir.');
    }
  }

  async function imprimirA4() {
    if (fotosImpressao) await aguardarFotosDaImpressao();
    void imprimirComClasse('imprimindo-a4');
  }

  async function salvarPdfCupom() {
    const pageSize = medirCupomPreview();
    const r = await salvarComoPdf(
      'imprimindo-cupom',
      `cupom-${detalhe?.numero ?? 'orcamento'}.pdf`,
      { pageSize, pageName: 'cupom-termico-58' },
    );
    if (!r.ok && !r.cancelado) {
      await dialogos.alertar(r.erro ?? 'Falha ao gerar PDF.');
    }
  }

  async function salvarPdfA4() {
    if (fotosImpressao) await aguardarFotosDaImpressao();
    const r = await salvarComoPdf(
      'imprimindo-a4',
      `orcamento-${detalhe?.numero ?? 'orcamento'}.pdf`,
    );
    if (!r.ok && !r.cancelado) {
      await dialogos.alertar(r.erro ?? 'Falha ao gerar PDF.');
    }
  }

  useEffect(() => {
    setCarregando(true);
    setErro(null);
    orcamentosApi
      .obter(id)
      .then(setDetalhe)
      .catch((e: unknown) => {
        setErro(e instanceof Error ? e.message : 'Falha ao carregar ordem de serviço.');
      })
      .finally(() => setCarregando(false));
  }, [id]);

  if (carregando) {
    return (
      <section className="page">
        <PageHeader titulo="Carregando ordem de serviço…" />
      </section>
    );
  }

  if (erro || !detalhe) {
    return (
      <section className="page">
        <PageHeader
          titulo="Ordem de serviço"
          acoes={<Button onClick={aoVoltar}>Voltar</Button>}
        />
        <p className="alert alert--error" role="alert">
          {erro ?? 'Ordem de serviço não encontrada.'}
        </p>
      </section>
    );
  }

  const pecas = detalhe.itens.filter((i) => i.tipo === 'Produto');
  const servicos = detalhe.itens.filter((i) => i.tipo === 'Servico');

  return (
    <section className="page">
      <PageHeader
        titulo={`Ordem de serviço ${detalhe.numero}`}
        acoes={
          <>
            <BotaoOcultarValores />
            <Button onClick={aoVoltar}>Voltar</Button>
            <Button onClick={() => aoEditar(detalhe.id)}>Editar</Button>
            <Button onClick={() => setPreVisualizandoA4(true)}>Imprimir A4</Button>
            <Button onClick={abrirPreviaCupom}>Imprimir cupom 58mm</Button>
            <Button onClick={() => setEnviandoWhatsapp(true)}>Enviar no WhatsApp</Button>
            <Button onClick={() => void copiarLinkAcompanhamento()} disabled={gerandoLink}>
              {gerandoLink ? 'Gerando…' : 'Copiar link de acompanhamento'}
            </Button>
            <Button variante="primary" onClick={() => setCriandoContaReceber(true)}>
              + Criar conta a receber
            </Button>
          </>
        }
      />

      {linkMsg && (
        <p
          role={linkMsg.tipo === 'erro' ? 'alert' : 'status'}
          className={linkMsg.tipo === 'erro' ? 'alert alert--error' : ''}
          style={
            linkMsg.tipo === 'ok'
              ? { color: 'var(--color-success)', fontWeight: 600, wordBreak: 'break-all' }
              : { wordBreak: 'break-all' }
          }
        >
          {linkMsg.tipo === 'ok' ? '✓ ' : ''}
          {linkMsg.texto}
        </p>
      )}

      {!detalhe.colaboradorResponsavelId && !etapaFinal && (
        // O.S. antiga, de antes do responsável ser obrigatório. Fechada/paga
        // ou cancelada fica como está; aberta precisa ser preenchida.
        <div
          className="info-banner info-banner--alerta"
          role="status"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>Esta O.S. está sem colaborador responsável.</span>
          <Button onClick={() => aoEditar(detalhe.id)}>Definir responsável</Button>
        </div>
      )}

      <div className="card">
        <div className="card__header">
          <span className="card__title">Status da ordem de serviço</span>
        </div>
        <div className="card__body">
          <div className="field" style={{ maxWidth: 380 }}>
            <label className="field__label" htmlFor="orcamento-status">
              Situação atual
            </label>
            <select
              id="orcamento-status"
              className="select"
              value={detalhe.status}
              disabled={salvandoStatus || etapaFinal}
              onChange={(e) => void aoMudarStatus(e.target.value as StatusOrcamento)}
            >
              {etapas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.rotulo}
                </option>
              ))}
            </select>
            <span className="field__hint">
              {salvandoStatus
                ? 'Salvando…'
                : etapaFinal
                  ? `${rotuloDe(detalhe.status)} é uma etapa final — a ordem fica somente para consulta.`
                  : 'Alterar o status aplica baixa/estorno de estoque automaticamente.'}
            </span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <span className="card__title">Cliente</span>
        </div>
        <div className="card__body">
          <div className="detalhe-info-grid">
            <Info rotulo="Nome" valor={detalhe.clienteNome} />
            <Info rotulo="Documento" valor={detalhe.clienteDocumento ?? '—'} />
            <Info rotulo="E-mail" valor={detalhe.clienteEmail ?? '—'} />
            <Info rotulo="Telefone" valor={detalhe.clienteTelefone ?? '—'} />
            <Info
              rotulo={detalhe.placa ? 'Placa' : 'Veículo'}
              valor={detalhe.veiculoDescricao}
            />
            <Info
              rotulo="Quilometragem"
              valor={
                detalhe.kmRegistrado != null
                  ? `${detalhe.kmRegistrado.toLocaleString('pt-BR')} km`
                  : '—'
              }
            />
            <Info rotulo="Responsável" valor={detalhe.colaboradorResponsavelNome ?? '—'} />
            <Info rotulo="Data" valor={formatarData(detalhe.data)} />
            <Info rotulo="Prazo de entrega" valor={formatarData(detalhe.prazoEntrega)} />
            <Info
              rotulo="Observações"
              valor={detalhe.observacoes ?? '—'}
            />
          </div>
          {detalhe.veiculoId && (
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Button onClick={abrirRegistroKm}>
                {detalhe.kmRegistrado != null ? 'Corrigir quilometragem' : 'Registrar quilometragem'}
              </Button>
              <span className="field__hint" style={{ margin: 0 }}>
                A leitura fica gravada nesta ordem e entra no histórico do veículo.
              </span>
            </div>
          )}
        </div>
      </div>

      <GaleriaFotosOS
        orcamentoId={detalhe.id}
        numero={detalhe.numero}
        placa={detalhe.placa}
        aoMudarFotos={setFotosOS}
      />

      {/* Some sozinho sem pós-venda; offline vira aviso discreto. */}
      <CardAvaliacaoOS orcamentoId={detalhe.id} clienteNome={detalhe.clienteNome} />

      <SecaoItens titulo="Peças" itens={pecas} />
      <SecaoItens titulo="Serviços" itens={servicos} />

      {(() => {
        const totaisPecas = somarSecao(pecas);
        const totaisServicos = somarSecao(servicos);
        return (
          <div className="totais-cards-grid">
            <CardTotalSecao titulo="Total de peças" totais={totaisPecas} />
            <CardTotalSecao titulo="Total de serviços" totais={totaisServicos} />

            <div className="card totais-card-geral">
              <div className="card__header">
                <span className="card__title">Total de peças e serviços</span>
              </div>
              <div className="card__body totais-rodape">
                <div className="totais-rodape__linha">
                  <span>Total de peças</span>
                  <strong>{formatadorMoeda.format(totaisPecas.comDesconto)}</strong>
                </div>
                <div className="totais-rodape__linha">
                  <span>Total de serviços</span>
                  <strong>{formatadorMoeda.format(totaisServicos.comDesconto)}</strong>
                </div>
                <div className="totais-rodape__linha">
                  <span>Subtotal</span>
                  <strong>{formatadorMoeda.format(detalhe.subtotal)}</strong>
                </div>
                <div className="totais-rodape__linha">
                  <span>
                    Desconto geral{' '}
                    {formatarDesconto(detalhe.descontoGeralTipo, detalhe.descontoGeralValor)}
                  </span>
                  <strong>− {formatadorMoeda.format(detalhe.valorDescontoGeral)}</strong>
                </div>
                <div className="totais-rodape__linha totais-rodape__total">
                  <span>Total final</span>
                  <strong>{formatadorMoeda.format(detalhe.total)}</strong>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {criandoContaReceber && (
        <CriarContaReceberDoOrcamentoModal
          orcamentoId={detalhe.id}
          aoFechar={() => setCriandoContaReceber(false)}
        />
      )}

      <EnviarOrcamentoWhatsappModal
        aberto={enviandoWhatsapp}
        onFechar={() => setEnviandoWhatsapp(false)}
        dados={{
          id: detalhe.id,
          numero: detalhe.numero,
          clienteNome: detalhe.clienteNome,
          clienteTelefone: detalhe.clienteTelefone,
          placa: detalhe.placa,
          veiculoDescricao: detalhe.veiculoDescricao,
          data: detalhe.data,
          total: detalhe.total,
          colaboradorResponsavelNome: detalhe.colaboradorResponsavelNome,
          itens: detalhe.itens.map((it) => ({
            tipo: it.tipo,
            descricao: it.descricao,
            quantidade: it.quantidade,
          })),
        }}
      />

      <PrintPortal>
        <CupomTermico58mm
          detalhe={detalhe}
          nomeEmpresa={nomeEmpresa}
          empresa={empresa}
          termo={termo58mm}
          linkAcompanhamento={linkAcompanhamento}
        />
        <OrcamentoImpressaoA4
          detalhe={detalhe}
          nomeEmpresa={nomeEmpresa}
          empresa={empresa}
          termo={termoA4}
          fotos={fotosImpressao}
        />
      </PrintPortal>

      {registrandoKm && (
        <Modal
          aberto
          tamanho="sm"
          titulo={
            detalhe.kmRegistrado != null
              ? 'Corrigir quilometragem da ordem'
              : 'Registrar quilometragem da ordem'
          }
          onFechar={() => setRegistrandoKm(false)}
          rodape={
            <>
              <Button onClick={() => setRegistrandoKm(false)} disabled={salvandoKm}>
                Cancelar
              </Button>
              <Button variante="primary" onClick={() => void salvarKm()} disabled={salvandoKm}>
                {salvandoKm ? 'Salvando…' : 'Salvar'}
              </Button>
            </>
          }
        >
          <div className="field">
            <label className="field__label" htmlFor="orcamento-km">
              Quilometragem medida nesta ordem
            </label>
            <input
              id="orcamento-km"
              className="input"
              inputMode="numeric"
              autoFocus
              value={kmDigitado}
              onChange={(e) => setKmDigitado(e.target.value.replace(/\D/g, '').slice(0, 7))}
              placeholder="Ex.: 87450"
            />
            <span className="field__hint">
              Este número fica preso a esta ordem de serviço: ordens futuras do mesmo
              veículo não mudam o que está aqui.
            </span>
          </div>
          {erroKm && (
            <p role="alert" className="alert alert--error">
              {erroKm}
            </p>
          )}
        </Modal>
      )}

      {preVisualizandoCupom && (
        <Modal
          aberto
          tamanho="sm"
          titulo="Pré-visualização — cupom 58mm"
          onFechar={() => setPreVisualizandoCupom(false)}
          rodape={
            <>
              <Button onClick={() => setPreVisualizandoCupom(false)}>Fechar</Button>
              <Button onClick={() => void salvarPdfCupom()}>Salvar PDF</Button>
              <Button variante="primary" onClick={() => void imprimirCupom()}>
                Imprimir
              </Button>
            </>
          }
        >
          {linkCupomFalhou && (
            <p className="alert alert--error" role="alert">
              Não foi possível gerar o link de acompanhamento — o cupom sairá sem o
              QR code. Conecte à internet, sincronize a ordem de serviço e abra a
              pré-visualização de novo.
            </p>
          )}
          <div className="preview-impressao preview-impressao--58mm">
            <CupomTermico58mm
              detalhe={detalhe}
              nomeEmpresa={nomeEmpresa}
              empresa={empresa}
              termo={termo58mm}
              linkAcompanhamento={linkAcompanhamento}
            />
          </div>
        </Modal>
      )}

      {preVisualizandoA4 && (
        <Modal
          aberto
          tamanho="lg"
          titulo="Pré-visualização — ordem de serviço A4"
          onFechar={() => setPreVisualizandoA4(false)}
          rodape={
            <>
              <Button onClick={() => setPreVisualizandoA4(false)}>Fechar</Button>
              <Button onClick={() => void salvarPdfA4()}>Salvar PDF</Button>
              <Button variante="primary" onClick={() => void imprimirA4()}>
                Imprimir
              </Button>
            </>
          }
        >
          {fotosOS.length > 0 && (
            <label className="checkbox" style={{ minHeight: 44, marginBottom: 12 }}>
              <input
                type="checkbox"
                checked={incluirFotos}
                onChange={(e) => alterarIncluirFotos(e.target.checked)}
              />
              <span>
                Incluir fotos na impressão ({fotosOS.length}{' '}
                {fotosOS.length === 1 ? 'foto' : 'fotos'})
              </span>
            </label>
          )}
          <div className="preview-impressao preview-impressao--a4">
            <OrcamentoImpressaoA4
              detalhe={detalhe}
              nomeEmpresa={nomeEmpresa}
              empresa={empresa}
              termo={termoA4}
              fotos={fotosImpressao}
            />
          </div>
        </Modal>
      )}
    </section>
  );
}

function Info({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>{rotulo}</div>
      <div>
        <strong>{valor}</strong>
      </div>
    </div>
  );
}

type TotaisSecao = { bruto: number; desconto: number; comDesconto: number };

function arred(v: number): number {
  return Math.round(v * 100) / 100;
}

function somarSecao(itens: OrcamentoDetalhe['itens']): TotaisSecao {
  let bruto = 0;
  let desconto = 0;
  for (const i of itens) {
    bruto += i.subtotalBruto;
    desconto += i.valorDesconto;
  }
  return {
    bruto: arred(bruto),
    desconto: arred(desconto),
    comDesconto: arred(bruto - desconto),
  };
}

function CardTotalSecao({ titulo, totais }: { titulo: string; totais: TotaisSecao }) {
  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">{titulo}</span>
      </div>
      <div className="card__body totais-rodape">
        <div className="totais-rodape__linha">
          <span>Subtotal</span>
          <strong>{formatadorMoeda.format(totais.bruto)}</strong>
        </div>
        <div className="totais-rodape__linha">
          <span>Desconto</span>
          <strong>− {formatadorMoeda.format(totais.desconto)}</strong>
        </div>
        <div className="totais-rodape__linha totais-rodape__total">
          <span>Total</span>
          <strong>{formatadorMoeda.format(totais.comDesconto)}</strong>
        </div>
      </div>
    </div>
  );
}

function SecaoItens({
  titulo,
  itens,
}: {
  titulo: string;
  itens: OrcamentoDetalhe['itens'];
}) {
  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">{titulo}</span>
      </div>
      <div className="card__body">
        {itens.length === 0 ? (
          <p style={{ opacity: 0.7 }}>Nenhum item.</p>
        ) : (
          <div className="table-scroll"><table className="table table--responsive">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Colaborador</th>
                <th className="table__numeric">Qtd</th>
                <th className="table__numeric">Preço unit.</th>
                <th>Desconto</th>
                <th className="table__numeric">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => (
                <tr key={i.id}>
                  <td className="table__primary">{i.descricao}</td>
                  <td className="table__muted" data-label="Colaborador">{i.colaboradorNome ?? '—'}</td>
                  <td className="table__numeric" data-label="Quantidade">{i.quantidade}</td>
                  <td className="table__numeric" data-label="Preço unitário">
                    {formatadorMoeda.format(i.precoUnitario)}
                  </td>
                  <td data-label="Desconto">{formatarDesconto(i.descontoTipo, i.descontoValor)}</td>
                  <td className="table__numeric" data-label="Subtotal">{formatadorMoeda.format(i.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
